import { usePlatformSetting } from "./usePlatformSettings";
import { usePublicPlatformSettings } from "./usePublicPlatformSettings";
import { useAuthSession } from "./useAuthSession";

// Default fallback values
const DEFAULTS = {
  platformName: 'Neeli Ai',
  platformTagline: 'Your Personal AI Assistant',
  supportEmail: 'support@neeli.ai',
} as const;

export function usePlatformBranding() {
  const { data: session } = useAuthSession();
  
  // Use public settings (edge function) when not authenticated
  // This ensures logos work on auth/landing pages
  const { data: publicSettings, isLoading: publicLoading } = usePublicPlatformSettings();
  
  // Use direct DB query when authenticated (faster, cached)
  const { data: nameSetting, isLoading: nameLoading } = usePlatformSetting('platform_name');
  const { data: taglineSetting, isLoading: taglineLoading } = usePlatformSetting('platform_tagline');
  const { data: logoSetting, isLoading: logoLoading } = usePlatformSetting('platform_logo_url');
  const { data: markLogoSetting, isLoading: markLogoLoading } = usePlatformSetting('platform_mark_logo_url');
  const { data: supportEmailSetting, isLoading: emailLoading } = usePlatformSetting('support_email');
  
  const hasValue = (value: any): boolean => Boolean(value && value !== '' && value !== 'null');
  
  // If authenticated, prefer DB values; otherwise use public settings from edge function
  if (session?.user) {
    return {
      platformName: hasValue(nameSetting?.setting_value) ? nameSetting!.setting_value : DEFAULTS.platformName,
      platformTagline: hasValue(taglineSetting?.setting_value) ? taglineSetting!.setting_value : DEFAULTS.platformTagline,
      logoUrl: hasValue(logoSetting?.setting_value) ? logoSetting!.setting_value : null,
      markLogoUrl: hasValue(markLogoSetting?.setting_value) ? markLogoSetting!.setting_value : null,
      supportEmail: hasValue(supportEmailSetting?.setting_value) ? supportEmailSetting!.setting_value : DEFAULTS.supportEmail,
      isLoading: nameLoading || taglineLoading || logoLoading || markLogoLoading || emailLoading,
    };
  }
  
  // Not authenticated - use public settings from edge function
  return {
    platformName: hasValue(publicSettings?.platform_name) ? publicSettings!.platform_name : DEFAULTS.platformName,
    platformTagline: hasValue(publicSettings?.platform_tagline) ? publicSettings!.platform_tagline : DEFAULTS.platformTagline,
    logoUrl: hasValue(publicSettings?.platform_logo_url) ? publicSettings!.platform_logo_url : null,
    markLogoUrl: hasValue(publicSettings?.platform_mark_logo_url) ? publicSettings!.platform_mark_logo_url : null,
    supportEmail: hasValue(publicSettings?.support_email) ? publicSettings!.support_email : DEFAULTS.supportEmail,
    isLoading: publicLoading,
  };
}
